import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import type { CourseSummary, Round } from '@/lib/api/types';
import { BridgeWebEventType } from '@pintrack/bridge';
import { bridgeRequest, inNativeShell } from '@/lib/bridge/bridgeClient';
import { cx } from '@/lib/cx';
import { fmtDateLong } from '@/lib/format/date';
import { t } from '@/lib/i18n';
import {
  canvasToBlob,
  drawShareCard,
  shareFileName,
  SHARE_FORMATS,
  SHARE_LABEL,
  type ShareCardData,
  type ShareFormat,
} from '@/lib/share/shareCard';
import styles from './ShareRound.module.css';
import { CloseIcon } from '@/components/icons';

/**
 * Share sheet for a finished round (canvas 1i).
 *
 * Both formats are rendered up front rather than on demand: they are the product here, and
 * the canvas presents them side by side so the player picks the one that matches where they
 * are posting. Each `<canvas>` is the real 1080px artwork, scaled down by CSS for preview —
 * what you see is exactly the file you get.
 */
export function ShareRound({
  round,
  course,
  open,
  onClose,
}: {
  round: Round;
  course: CourseSummary | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const canvases = useRef<Partial<Record<ShareFormat, HTMLCanvasElement | null>>>({});
  const [busy, setBusy] = useState<ShareFormat | null>(null);
  /* Set once a canvas turns out to be tainted, so later renders skip the imagery outright. */
  const [imageryBlocked, setImageryBlocked] = useState(false);

  const data: ShareCardData = {
    courseName: course?.name ?? t('round.courseFallback'),
    dateLabel: fmtDateLong(round.completedAt ?? round.startedAt),
    teeLabel: t('share.tee', { name: round.teeBoxName }),
    strokes: round.totalStrokes,
    toPar: round.totalToPar,
    putts: round.stats.totalPutts,
    girPct: round.stats.greensInRegulationPct,
    fairwayPct: round.stats.fairwayHitPct,
    differential: round.scoreDifferential,
    // `par` rides on the score itself — the server derives it from the course, so the strip
    // never has to reach for the snapshot to know what a birdie was.
    holes: round.scores.map((s) => ({ number: s.holeNumber, strokes: s.strokes, par: s.par })),
    location: course?.location,
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Redraw whenever the sheet opens or the round changes. Drawing is async (webfonts and
  // imagery both have to land first), so this cannot happen during render.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    void (async () => {
      for (const format of ['square', 'story'] as const) {
        const canvas = canvases.current[format];
        if (!canvas || cancelled) continue;
        await drawShareCard(canvas, data, format, { withImagery: !imageryBlocked });
      }
    })();

    return () => {
      cancelled = true;
    };
    // `data` is rebuilt each render; the round id and course are what actually change it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, round.id, course?.id, imageryBlocked]);

  const download = async (format: ShareFormat) => {
    const canvas = canvases.current[format];
    if (!canvas) return;

    setBusy(format);
    try {
      let blob: Blob | null;
      try {
        blob = await canvasToBlob(canvas);
      } catch {
        /*
         * A tainted canvas — the imagery came from a host that served no CORS header. The
         * card is still worth having without the aerial, so redraw flat and export that
         * rather than failing the download.
         */
        setImageryBlocked(true);
        await drawShareCard(canvas, data, format, { withImagery: false });
        blob = await canvasToBlob(canvas);
        toast.success(t('share.withoutImagery'));
      }

      if (!blob) throw new Error(t('share.imageFailed'));

      const fileName = shareFileName(data.courseName, data.dateLabel, format);

      /*
       * Dentro da casca nativa, `<a download>` **não faz nada**: um WebView não tem pasta de
       * downloads para onde apontar, e o clique morre em silêncio — o pior tipo de falha, porque
       * o botão parece ter funcionado. Ali a entrega é o share sheet do sistema, que além de
       * salvar também manda para WhatsApp, Instagram e o resto, que é o que alguém quer fazer
       * com um cartão de volta.
       *
       * `toDataURL` e não o blob: a ponte transporta string, e converter blob → base64 aqui só
       * acrescentaria um passo assíncrono a algo que o canvas já sabe fazer numa chamada.
       */
      if (inNativeShell) {
        const reply = await bridgeRequest(BridgeWebEventType.Share, {
          dataUrl: canvas.toDataURL('image/png'),
          fileName,
          text: `${data.courseName} · ${data.dateLabel}`,
        });
        // Cancelar o share sheet volta `ok: false` sem `error` — não é falha para mostrar.
        if (reply && !reply.ok && reply.error) toast.error(reply.error);
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('share.downloadFailed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <dialog ref={dialogRef} className={styles.dialog} onClose={onClose}>
      <div className={styles.head}>
        <div>
          <h2 className={styles.title}>{t('round.share')}</h2>
          <p className={styles.subtitle}>{t('share.subtitle')}</p>
        </div>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={t('share.close')}
        >
          <CloseIcon size={18} />
        </button>
      </div>

      <div className={styles.cards}>
        {(['square', 'story'] as const).map((format) => (
          <figure key={format} className={cx(styles.card, styles[format])}>
            <canvas
              ref={(el) => {
                canvases.current[format] = el;
              }}
              className={styles.canvas}
              width={SHARE_FORMATS[format].width}
              height={SHARE_FORMATS[format].height}
              // The card is decorative here; its content is the round already on the page.
              role="presentation"
            />
            <figcaption className={styles.caption}>{t(SHARE_LABEL[format])}</figcaption>
            <Button
              variant="secondary"
              block
              onClick={() => void download(format)}
              disabled={busy !== null}
            >
              {/*
                Na casca o botão não baixa, compartilha — e prometer "Baixar PNG" para depois
                abrir um share sheet é dizer ao usuário algo diferente do que vai acontecer.
                `round.share` já existe e diz exatamente isso.
              */}
              {busy === format
                ? t('share.generating')
                : inNativeShell
                  ? t('round.share')
                  : t('share.download')}
            </Button>
          </figure>
        ))}
      </div>

      {imageryBlocked && (
        <p className={styles.note}>{t('share.blockedNote')}</p>
      )}
    </dialog>
  );
}
